import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { ip_back } from "../config";
import {
  buildKotPlayerMatchRow,
  formatKotPlayerDisplayName,
  resolveOpponentNameFromMatch,
  sortKotPlayerMatchRows,
} from "../utils/kotPlayerMatches";

function authHeaders() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

function outcomeLabel(outcome, t) {
  if (outcome === "win") return t("kotPlayerMatchResultWin") || "Won";
  if (outcome === "loss") return t("kotPlayerMatchResultLoss") || "Lost";
  if (outcome === "abandoned") return t("kotMatchAbandoned") || "Abandoned";
  if (outcome === "pending") return t("kotPlayerMatchResultPending") || "Pending";
  return "—";
}

function outcomeClass(outcome) {
  if (outcome === "win") return "kot-player-matches__outcome--win";
  if (outcome === "loss") return "kot-player-matches__outcome--loss";
  if (outcome === "pending") return "kot-player-matches__outcome--pending";
  return "";
}

/**
 * Dialog listing all KoT matches for one player in an exercise.
 */
export function KotPlayerMatchesDialog({
  open,
  onClose,
  playerRow,
  teId,
  tsId,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [matches, setMatches] = useState([]);

  const playerId = playerRow?.player_id ? String(playerRow.player_id) : null;
  const playerName = formatKotPlayerDisplayName(playerRow);

  const loadMatches = useCallback(async () => {
    if (!teId || !tsId || !playerId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${ip_back}api/kot/playerMatches`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          te_id: teId,
          ts_id: tsId,
          player_id: playerId,
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json.status !== "success") {
        throw new Error(json.message || "Failed to load matches");
      }
      setMatches(Array.isArray(json.data?.matches) ? json.data.matches : []);
    } catch (e) {
      setMatches([]);
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [teId, tsId, playerId]);

  useEffect(() => {
    if (!open || !playerId) return;
    loadMatches();
  }, [open, playerId, loadMatches]);

  useEffect(() => {
    if (!open) {
      setMatches([]);
      setError(null);
    }
  }, [open]);

  const displayRows = useMemo(() => {
    if (!playerId || !matches.length) return [];
    const nameById = new Map();
    for (const m of matches) {
      if (m.player_a?.player_id) {
        nameById.set(
          String(m.player_a.player_id),
          formatKotPlayerDisplayName(m.player_a)
        );
      }
      if (m.player_b?.player_id) {
        nameById.set(
          String(m.player_b.player_id),
          formatKotPlayerDisplayName(m.player_b)
        );
      }
    }
    const resolveName = (oppId, match) =>
      resolveOpponentNameFromMatch(match, oppId, nameById);
    return sortKotPlayerMatchRows(
      matches.map((m) => buildKotPlayerMatchRow(m, playerId, resolveName))
    );
  }, [matches, playerId]);

  const statsLine = playerRow
    ? `${t("points") || "Pts"}: ${playerRow.total_points ?? 0} · ${t("won") || "W"}: ${playerRow.matches_won ?? 0} · ${t("lost") || "L"}: ${playerRow.matches_lost ?? 0}`
    : null;

  return (
    <Dialog
      open={Boolean(open && playerId)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="kot-player-matches-dialog"
    >
      <DialogTitle className="kot-player-matches__title">
        <span>
          {t("kotPlayerMatchHistory") || "Match history"} — {playerName}
        </span>
        <IconButton
          aria-label={t("close") || "Close"}
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className="kot-player-matches__content">
        {statsLine ? (
          <p className="kot-player-matches__stats">{statsLine}</p>
        ) : null}
        {loading ? (
          <div className="kot-player-matches__loading">
            <CircularProgress size={28} />
          </div>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}
        {!loading && !error && displayRows.length === 0 ? (
          <p className="kot-player-matches__empty">
            {t("kotPlayerMatchHistoryEmpty") ||
              "No matches recorded for this player yet."}
          </p>
        ) : null}
        {!loading && displayRows.length > 0 ? (
          <div className="kot-player-matches__table-wrap">
            <table className="kot-player-matches__table">
              <thead>
                <tr>
                  <th>{t("kotPlayerMatchRound") || "Round"}</th>
                  <th>{t("table") || "Table"}</th>
                  <th>{t("kotPlayerMatchOpponent") || "Opponent"}</th>
                  <th>{t("kotPlayerMatchScore") || "Score"}</th>
                  <th>{t("kotPlayerMatchResult") || "Result"}</th>
                  <th>{t("kotPlayerMatchPoints") || "Pts"}</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row) => (
                  <tr key={row.kot_match_id}>
                    <td>{row.round_number ?? "—"}</td>
                    <td>{row.table_number ?? "—"}</td>
                    <td>{row.opponent_name}</td>
                    <td className="kot-player-matches__score">{row.score_label}</td>
                    <td>
                      <span
                        className={`kot-player-matches__outcome ${outcomeClass(row.outcome)}`}
                      >
                        {outcomeLabel(row.outcome, t)}
                      </span>
                    </td>
                    <td>
                      {row.points != null ? row.points : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close") || "Close"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default KotPlayerMatchesDialog;
